import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { useWizardStore, type PlanSlug, type SiteTypeChoice } from "@/state/wizard-store";
import { api, ApiError, type Addon, type PromoTerms } from "@/lib/api-client";

/**
 * Plan bullets are DERIVED from the platform's own limits, never hardcoded.
 *
 * The static list this replaces had drifted into advertising things that were
 * not true at the point of purchase: "Up to 100 products" against a real cap of
 * 1,000, "Unlimited products" against 10,000, "Multiple sites" when migration
 * 203 made every plan exactly one site, "Custom domain" as a Growth perk when
 * it ships on all three, and a "Dedicated success manager" that does not exist
 * (it is on the knowledge base's own deny-list of false claims).
 *
 * Deriving from `features` means a plan change on the platform needs no app
 * deploy — the same rule the add-on promo badge already follows.
 */
const SUPPORT_LABELS: Record<string, string> = {
  standard: "AI assistant + tickets",
  priority: "Priority support",
  priority_onboarding: "Priority support + onboarding session",
};

/** Gated capability keys (migration 203) → what to call them for a customer. */
const CAPABILITY_LABELS: Record<string, string> = {
  dippy: "Dippy store agent",
  addie: "Addie ads agent",
  meilisearch: "Fast product search",
  volume_discounts: "Volume discounts",
  byok: "Bring your own AI key",
  multi_channel: "Multi-channel selling",
  api_access: "API access + webhooks",
  video: "AI video",
};

/**
 * Last-resort bullets for a platform that serves no `features` block. Kept
 * deliberately vague: a claim we cannot verify against the payload is exactly
 * how the previous list went wrong.
 */
const FALLBACK_BULLETS: Record<PlanSlug, string[]> = {
  starter: ["The complete platform", "Your own domain", "0% transaction fees"],
  growth: ["Everything in Starter", "The Dippy and Addie agents", "Priority support"],
  pro: ["Everything in Growth", "Multi-channel selling", "API access + webhooks"],
};

const BLURB_BY_PLAN: Record<PlanSlug, string> = {
  starter: "Everything you need to launch one site.",
  growth: "For growing brands — the agents that run the store.",
  pro: "For high-volume sellers — every channel, full API access.",
};

/** The same blurbs without the store wording, for a customer building something else. */
const NEUTRAL_BLURB_BY_PLAN: Record<PlanSlug, string> = {
  starter: "Everything you need to launch one site.",
  growth: "For growing sites — more AI, more room, more logins.",
  pro: "For high-volume sites — full API access and AI video.",
};

/**
 * What a plan limit or capability means depends on the kind of site. A
 * lodging or marketing site has no catalogue, so a product cap and the
 * store-only agents are noise on its plan card. Site types not listed against
 * a capability do not get it advertised. Only applied once the customer has
 * told us their site type (they arrive with one from the marketing site);
 * until then the card shows everything, as before.
 */
const CATALOGUE_SITE_TYPES: readonly SiteTypeChoice[] = ["ecommerce", "museum"];
const CAPABILITY_SITE_TYPES: Record<string, readonly SiteTypeChoice[]> = {
  dippy: ["ecommerce"],
  meilisearch: ["ecommerce"],
  volume_discounts: ["ecommerce"],
  multi_channel: ["ecommerce"],
  addie: ["ecommerce", "lodging"],
};

/**
 * The line under a plan's name. The platform's own plan descriptions are
 * written for stores ("the AI agents that run the store", "high-volume
 * sellers"), so they are only used for a store, or when the customer has not
 * said what they are building. Any other site type gets the neutral wording.
 */
function planBlurb(
  slug: PlanSlug,
  description: string | null | undefined,
  siteType: SiteTypeChoice | undefined,
): string {
  if (siteType && siteType !== "ecommerce") return NEUTRAL_BLURB_BY_PLAN[slug];
  return description || BLURB_BY_PLAN[slug];
}

function capabilityApplies(key: string, siteType: SiteTypeChoice | undefined): boolean {
  const only = CAPABILITY_SITE_TYPES[key];
  return !siteType || !only || only.includes(siteType);
}

function numOrNull(v: unknown): number | null {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function capabilityKeys(features: Record<string, unknown> | undefined): string[] {
  const raw = features?.features;
  return Array.isArray(raw) ? raw.filter((k): k is string => typeof k === "string") : [];
}

/**
 * Bullets for one plan card. `lowerTierCaps` are the capabilities already
 * included by the tier below, so each card advertises what it ADDS rather than
 * repeating the whole ladder.
 */
function planBullets(
  slug: PlanSlug,
  features: Record<string, unknown> | undefined,
  lowerTierCaps: Set<string>,
  siteType?: SiteTypeChoice,
): string[] {
  if (!features || Object.keys(features).length === 0) return FALLBACK_BULLETS[slug];
  const hasCatalogue = !siteType || CATALOGUE_SITE_TYPES.includes(siteType);

  const out: string[] = [];
  const credits = numOrNull(features.ai_credits_monthly);
  if (credits !== null) out.push(`${credits.toLocaleString()} AI credits a month`);

  const products = hasCatalogue ? numOrNull(features.max_products) : null;
  const storage = numOrNull(features.max_storage_gb);
  if (products !== null && storage !== null) {
    out.push(`${products.toLocaleString()} products, ${storage} GB storage`);
  } else if (products !== null) {
    out.push(`${products.toLocaleString()} products`);
  } else if (storage !== null) {
    out.push(`${storage} GB storage`);
  }

  const staff = numOrNull(features.max_staff);
  if (staff !== null) out.push(`${staff} staff account${staff === 1 ? "" : "s"}`);

  const added = capabilityKeys(features)
    .filter((k) => !lowerTierCaps.has(k) && capabilityApplies(k, siteType))
    .map((k) => CAPABILITY_LABELS[k])
    .filter((label): label is string => Boolean(label));
  if (added.length) {
    const shown = added.slice(0, 3).join(", ");
    out.push(added.length > 3 ? `${shown} +${added.length - 3} more` : shown);
  }

  const support = typeof features.support === "string" ? SUPPORT_LABELS[features.support] : undefined;
  if (support) out.push(support);

  // Never render an empty card body.
  return out.length ? out : FALLBACK_BULLETS[slug];
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

/** Like formatPrice but keeps cents when a discount produces them ($14.50). */
function formatMoney(cents: number): string {
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars.toFixed(0) : dollars.toFixed(2)}`;
}

type BillingInterval = "monthly" | "annual";

/** The price of one add-on for the interval being bought, in cents. */
function addonPriceCents(addon: Addon, interval: BillingInterval): number {
  return interval === "annual" ? addon.annual_price_cents : addon.monthly_price_cents;
}

/** Price of an add-on during its intro window, in cents, for either interval. */
function introPriceCents(addon: Addon, interval: BillingInterval = "monthly"): number {
  const pct = addon.intro_percent_off ?? 0;
  const base = addonPriceCents(addon, interval);
  return pct > 0 ? Math.round(base * (1 - pct / 100)) : base;
}

/**
 * Promo badge copy, derived entirely from the catalog payload — never
 * hardcoded, so a pricing change on the platform needs no app deploy. Returns
 * null when the add-on carries no intro promo.
 */
function introBadge(addon: Addon): string | null {
  const pct = addon.intro_percent_off ?? 0;
  const months = addon.intro_duration_months ?? 0;
  if (pct <= 0 || months <= 0) return null;
  return `${pct}% off your first ${months} month${months === 1 ? "" : "s"}`;
}

/** Tiers of one product share a family; an add-on without one is its own. */
function familyOf(addon: Addon): string {
  return addon.product_family ?? addon.slug;
}

/**
 * Heading for a tier group: the words the tier names share ("eTaxEngine
 * Standard" + "eTaxEngine Scale" → "eTaxEngine"), falling back to the first
 * tier's name when nothing overlaps.
 */
function tierGroupLabel(group: Addon[]): string {
  const words = group.map((a) => a.name.split(" "));
  const common: string[] = [];
  for (let i = 0; ; i++) {
    const w = words[0]?.[i];
    if (!w || !words.every((ws) => ws[i] === w)) break;
    common.push(w);
  }
  return common.length > 0 ? common.join(" ") : (group[0]?.name ?? "");
}

export function WelcomePlan() {
  const navigate = useNavigate();
  const setPlan = useWizardStore((s) => s.setPlan);
  const currentPlan = useWizardStore((s) => s.plan);
  const selectedAddons = useWizardStore((s) => s.addons) ?? [];
  const toggleAddon = useWizardStore((s) => s.toggleAddon);
  const setAddons = useWizardStore((s) => s.setAddons);
  // Set when the customer arrived from a site-type page on rumio.world.
  const siteType = useWizardStore((s) => s.questionnaire.siteType);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Promo code: typed here, checked against the platform on Apply, and sent
  // with checkout. Terms are interval-agnostic (monthly → repeating months,
  // annual → the first invoice), so an applied code survives the toggle.
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState<PromoTerms | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["plans"],
    queryFn: api.plans,
    staleTime: 60_000,
  });

  // Monthly is the default because it is the lower-commitment choice; annual
  // is a deliberate opt-in. Named setBillingInterval rather than setInterval so
  // it can't be confused with the DOM timer.
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");

  const plans =
    data?.plans.filter((p): p is typeof p & { slug: PlanSlug } =>
      p.slug === "starter" || p.slug === "growth" || p.slug === "pro",
    ) ?? [];
  // Feature detection: no add-on catalog (a platform predating the bundle
  // work) → the plan picker renders exactly as it always has, one CTA per card.
  const addons = data?.addons ?? [];
  const hasAddons = addons.length > 0;

  // In bundle mode the CTA lives below the add-ons, so pre-select the
  // recommended plan to make it immediately actionable.
  useEffect(() => {
    if (hasAddons && !currentPlan && data?.plans.some((p) => p.slug === "growth")) {
      setPlan("growth");
    }
  }, [hasAddons, currentPlan, data, setPlan]);

  async function startCheckout(plan: PlanSlug, withAddons: string[]) {
    setSubmitting(true);
    setError(null);
    setPlan(plan);
    try {
      // The 4th argument only exists when a code was applied, so the call
      // (and the request body) is identical to before for everyone else.
      const { checkout_url } = promo
        ? await api.checkout(plan, withAddons, billingInterval, promo.code)
        : await api.checkout(plan, withAddons, billingInterval);
      if (checkout_url.startsWith("http")) {
        window.location.href = checkout_url;
      } else {
        navigate(checkout_url);
      }
    } catch (err) {
      // A plan is already paid for and waiting for its site: there is nothing
      // to buy, only a site to build.
      if (err instanceof ApiError && err.code === "site_pending") {
        navigate("/questionnaire");
        return;
      }
      setError(err instanceof ApiError ? err.message : "Couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  }

  async function applyPromo() {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoBusy(true);
    setPromoError(null);
    try {
      const terms = await api.validatePromo(code);
      setPromo(terms);
      setPromoInput(terms.code);
    } catch (err) {
      setPromo(null);
      setPromoError(err instanceof ApiError ? err.message : "Couldn't check that code. Please try again.");
    } finally {
      setPromoBusy(false);
    }
  }

  function clearPromo() {
    setPromo(null);
    setPromoInput("");
    setPromoError(null);
  }

  // Checking a tier drops any sibling tier already in the basket (radio
  // semantics within a product family — checkout rejects two tiers of one
  // product); checking/unchecking anything else behaves as before.
  function selectAddon(addon: Addon) {
    if (selectedAddons.includes(addon.slug)) {
      toggleAddon(addon.slug);
      return;
    }
    const family = familyOf(addon);
    const siblings = addons
      .filter((x) => x.slug !== addon.slug && familyOf(x) === family)
      .map((x) => x.slug);
    setAddons([...selectedAddons.filter((s) => !siblings.includes(s)), addon.slug]);
  }

  // Family groups in catalog order: a group of one renders as a plain
  // checkbox card; tiers render together under a pick-one caption.
  const addonGroups: Addon[][] = [];
  {
    const byFamily = new Map<string, Addon[]>();
    for (const a of addons) {
      const group = byFamily.get(familyOf(a));
      if (group) {
        group.push(a);
      } else {
        const fresh = [a];
        byFamily.set(familyOf(a), fresh);
        addonGroups.push(fresh);
      }
    }
  }

  // Capabilities carried by every tier BELOW each plan, so a card lists what it
  // adds rather than repeating the ladder. plans arrive ordered by tier_level.
  const lowerCapsBySlug = new Map<string, Set<string>>();
  {
    const running = new Set<string>();
    for (const p of plans) {
      lowerCapsBySlug.set(p.slug, new Set(running));
      for (const k of capabilityKeys(p.features)) running.add(k);
    }
  }

  const chosen = plans.find((p) => p.slug === currentPlan) ?? null;
  const chosenAddons = addons.filter((a) => selectedAddons.includes(a.slug));
  const annual = billingInterval === "annual";
  const per = annual ? "/yr" : "/mo";
  const planCents = (annual ? chosen?.annual_price_cents : chosen?.monthly_price_cents) ?? 0;
  const listCents = planCents + chosenAddons.reduce((n, a) => n + addonPriceCents(a, billingInterval), 0);
  // The promo takes its percentage off the plan, and — only for a
  // plan_and_addons code — off each add-on's intro price as well (Stripe
  // stacks percent coupons multiplicatively, so this compounds the same way).
  const promoPct = promo?.percent_off ?? 0;
  const off = (cents: number) => Math.round(cents * (1 - promoPct / 100));
  const promoPlanCents = off(planCents);
  const introCents =
    promoPlanCents +
    chosenAddons.reduce((n, a) => {
      const intro = introPriceCents(a, billingInterval);
      return n + (promo?.applies_to === "plan_and_addons" ? off(intro) : intro);
    }, 0);
  // Longest promo window among the picked add-ons — the point at which the
  // bundle price steps up to list. A promo code's own window counts too.
  const introMonths = Math.max(
    chosenAddons.reduce(
      (n, a) => ((a.intro_percent_off ?? 0) > 0 ? Math.max(n, a.intro_duration_months ?? 0) : n),
      0,
    ),
    promo ? promo.duration_months : 0,
  );

  // Shared by both layouts (bundle summary card / plain plan grid).
  const promoBox = (
    <div className="mt-3">
      {promo ? (
        <p className="text-sm" data-testid="promo-applied">
          <span className="font-mono font-medium">{promo.code}</span> applied — {promo.percent_off}% off your{" "}
          {promo.applies_to === "plan_and_addons" ? "plan and partner products" : "plan"} for the first{" "}
          {annual ? "year" : `${promo.duration_months} months`}.{" "}
          <button type="button" className="underline" onClick={clearPromo} style={{ color: "var(--color-muted)" }}>
            Remove
          </button>
        </p>
      ) : (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void applyPromo();
          }}
        >
          <label htmlFor="promo-code" className="text-sm" style={{ color: "var(--color-muted)" }}>
            Have a promo code?
          </label>
          <input
            id="promo-code"
            name="promo-code"
            type="text"
            autoComplete="off"
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
            className="rounded-md border px-2 py-1 text-sm font-mono"
            style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-fg)" }}
            placeholder="CODE"
          />
          <Button type="submit" disabled={promoBusy || !promoInput.trim()}>
            {promoBusy ? "Checking…" : "Apply"}
          </Button>
        </form>
      )}
      {promoError && (
        <p className="mt-1 text-sm" style={{ color: "#dc2626" }} role="alert">
          {promoError}
        </p>
      )}
    </div>
  );

  return (
    <Shell
      title="Welcome to Rumio"
      subtitle="Pick a plan to start your 14-day free trial. No charge until day 14 — cancel anytime."
    >
      {!isLoading && !isError && plans.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <div
            className="inline-flex rounded-full border p-0.5"
            style={{ borderColor: "var(--color-border)" }}
            role="group"
            aria-label="Billing interval"
          >
            {(["monthly", "annual"] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setBillingInterval(opt)}
                aria-pressed={billingInterval === opt}
                className="rounded-full px-3 py-1 text-sm"
                style={
                  billingInterval === opt
                    ? { background: "var(--color-primary)", color: "white" }
                    : { color: "var(--color-muted)" }
                }
              >
                {opt === "monthly" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
          <span className="text-sm" style={{ color: "var(--color-muted)" }}>
            Yearly is ten times the monthly price — two months free.
          </span>
        </div>
      )}

      {isLoading && (
        <p className="text-sm" style={{ color: "var(--color-muted)" }}>Loading plans…</p>
      )}
      {isError && (
        <p className="text-sm" style={{ color: "#dc2626" }}>
          Couldn't load plans. Refresh to try again.
        </p>
      )}
      {data && (
        <div className="space-y-6">
          {/* Without an add-on catalog there is no summary card, so the promo
              box sits above the plan grid (its CTAs start checkout directly). */}
          {!hasAddons && promoBox}
          <div className="grid gap-4 md:grid-cols-3">
            {plans.map((p) => {
              const isRecommended = p.slug === "growth";
              const selected = p.slug === currentPlan;
              const body = (
                <>
                  <div className="mb-1 flex items-center gap-2">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                      {p.name}
                    </h3>
                    {isRecommended && (
                      <span
                        className="rounded-full px-2 py-0.5 text-xs"
                        style={{ background: "var(--color-accent)", color: "white" }}
                      >
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: "var(--color-muted)" }}>
                    {planBlurb(p.slug, p.description, siteType)}
                  </p>
                  <p className="mb-3 mt-2">
                    <span className="text-2xl font-semibold">
                      {formatPrice(annual ? p.annual_price_cents : p.monthly_price_cents)}
                    </span>
                    <span className="text-sm" style={{ color: "var(--color-muted)" }}>{per}</span>
                    {annual && (
                      <span className="ml-2 text-sm" style={{ color: "var(--color-muted)" }}>
                        ({formatMoney(Math.round(p.annual_price_cents / 12))}/mo)
                      </span>
                    )}
                  </p>
                  <ul className="mb-4 flex-1 space-y-1 text-sm">
                    {planBullets(p.slug, p.features, lowerCapsBySlug.get(p.slug) ?? new Set(), siteType).map((b) => (
                      <li key={b} className="flex items-start">
                        <span className="mr-2" style={{ color: "var(--color-primary)" }}>✓</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </>
              );
              const cardStyle = {
                borderColor: selected ? "var(--color-primary)" : "var(--color-border)",
                borderWidth: isRecommended || selected ? 2 : 1,
                background: "var(--color-card)",
              };

              // Bundle mode: the card selects, and the single CTA below (after
              // the add-ons) starts checkout. Otherwise: today's per-card CTA.
              return hasAddons ? (
                <button
                  key={p.slug}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setPlan(p.slug)}
                  className="rounded-lg border p-4 flex flex-col text-left"
                  style={cardStyle}
                >
                  {body}
                  <span className="text-sm font-medium" style={{ color: "var(--color-primary)" }}>
                    {selected ? "✓ Selected" : "Select"}
                  </span>
                </button>
              ) : (
                <div key={p.slug} className="rounded-lg border p-4 flex flex-col" style={cardStyle}>
                  {body}
                  <Button disabled={submitting} onClick={() => startCheckout(p.slug, [])}>
                    Start free trial
                  </Button>
                </div>
              );
            })}
          </div>

          {hasAddons && (
            <>
              <div>
                <h3 className="mb-1 text-lg font-semibold" style={{ fontFamily: "var(--font-display)" }}>
                  Partner products — member rate
                </h3>
                <p className="mb-3 text-sm" style={{ color: "var(--color-muted)" }}>
                  From our preferred partners, at a lower price than buying direct — on one bill.
                </p>
                <div className="space-y-2">
                  {addonGroups.map((group) => {
                    const cards = group.map((a) => {
                      const badge = introBadge(a);
                      const checked = selectedAddons.includes(a.slug);
                      return (
                        <label
                          key={a.slug}
                          className="flex cursor-pointer items-start gap-3 rounded-lg border p-4"
                          style={{
                            borderColor: checked ? "var(--color-primary)" : "var(--color-border)",
                            borderWidth: checked ? 2 : 1,
                            background: "var(--color-card)",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            onChange={() => selectAddon(a)}
                          />
                          <span className="flex-1">
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{a.name}</span>
                              {badge && (
                                <span
                                  className="rounded-full px-2 py-0.5 text-xs"
                                  style={{ background: "var(--color-accent)", color: "white" }}
                                >
                                  {badge}
                                </span>
                              )}
                            </span>
                            {a.description && (
                              <span className="mt-0.5 block text-sm" style={{ color: "var(--color-muted)" }}>
                                {a.description}
                              </span>
                            )}
                          </span>
                          <span className="whitespace-nowrap text-sm">
                            <span className="font-medium">
                              {formatMoney(addonPriceCents(a, billingInterval))}
                            </span>
                            <span style={{ color: "var(--color-muted)" }}>{per}</span>
                          </span>
                        </label>
                      );
                    });
                    if (group.length === 1) return cards;
                    return (
                      <div
                        key={familyOf(group[0]!)}
                        className="space-y-2 rounded-lg border p-3"
                        style={{ borderColor: "var(--color-border)" }}
                      >
                        <p
                          className="text-xs font-medium uppercase tracking-wide"
                          style={{ color: "var(--color-muted)" }}
                        >
                          {tierGroupLabel(group)} — pick one tier
                        </p>
                        {cards}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ borderColor: "var(--color-border)" }}>
                <p className="text-sm">
                  {chosen ? (
                    <>
                      <strong>{chosen.name}</strong>
                      {chosenAddons.length > 0 && (
                        <> + {chosenAddons.length} partner product{chosenAddons.length === 1 ? "" : "s"}</>
                      )}
                      {" — "}
                      <strong>{formatMoney(introCents)}{per}</strong>
                      {introMonths > 0 ? (
                        annual ? (
                          <>
                            {" "}on your first invoice, then {formatMoney(listCents)}{per}
                          </>
                        ) : (
                          <>
                            {" "}for your first {introMonths} month{introMonths === 1 ? "" : "s"}, then{" "}
                            {formatMoney(listCents)}{per}
                          </>
                        )
                      ) : null}
                      .
                    </>
                  ) : (
                    "Pick a plan above to continue."
                  )}
                </p>
                {promoBox}
                {promo && chosenAddons.some((a) => (a.intro_percent_off ?? 0) > 0) && (
                  <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
                    Stripe's checkout page lists the partner intro discount; your promo code is attached
                    right after checkout and shows on your first invoice.
                  </p>
                )}
                {error && (
                  <p className="mt-2 text-sm" style={{ color: "#dc2626" }}>
                    {error}
                  </p>
                )}
                <div className="mt-3">
                  <Button
                    disabled={!chosen || submitting}
                    onClick={() => chosen && startCheckout(chosen.slug, selectedAddons)}
                  >
                    Start free trial
                  </Button>
                </div>
              </div>
            </>
          )}

          {!hasAddons && error && (
            <p className="text-sm" style={{ color: "#dc2626" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </Shell>
  );
}
